<template>
  <div :class="classes">
    <div class="form form-horizontal">
      <template v-for="(field, fieldIndex) in form" v-if="meetsRequirements({requires: field.showIf || field.requires})">
        <template v-if="field.type === 'columns'">
          <h4 v-if="field.label">{{$t(field.label)}}</h4>
          <div class="layout-container">
            <div class="layout-column" v-for="(col, index) in field.options">
              <ProjectMasterDataTab
                      ref="forms"
                      :key="`${field.id || fieldIndex}__${col.id || index}`"
                      :form="col"
                      :form-values="formValues"
                      :item-nums="itemNums"
                      :itemNumsAll="itemNumsAll"
                      :has-order="hasOrder"
                      :is-editable="isEditable"
                      :force-validation="forceValidation"
                      v-on:input="handleFormInput"
                      v-on:valid="(isValid) => handleSubFormStatus(`${field.id || fieldIndex}__${col.id || index}`, isValid)"
              />
            </div>
          </div>
        </template>
        <template v-else-if="field.type === 'section' && Array.isArray(field.options)">
          <sect :title="$t(field.label)">
            <ProjectMasterDataTab
                    ref="forms"
                    :key="`${field.id || fieldIndex}__section`"
                    :form="field.options"
                    :form-values="formValues"
                    :item-nums="itemNums"
                    :item-nums-all="itemNumsAll"
                    :has-order="hasOrder"
                    :is-editable="isEditable"
                    :force-validation="forceValidation"
                    v-on:input="handleFormInput"
                    v-on:valid="(isValid) => handleSubFormStatus(`${field.id || fieldIndex}__section`, isValid)"
            />
          </sect>
        </template>
        <template v-else-if="field.type === 'section'">
          <h4>{{$t(field.label)}}</h4>
        </template>
        <template v-else-if="field.type === 'image'">
          <div :style="`text-align: ${field.align}`">
            <img :width="field.width" :height="field.height" :src="field.src" :alt="$t(field.label)"/>
          </div>
        </template>
        <template v-else>
          <field
                 :label-class="`col-md-12 col-sm-12 col-xs-12 ${field.labelClass}`"
                 field-class="col-md-12 col-sm-12 col-xs-12"
                 :name="field.id"
                 :label="$root.getLabelText(field.label)"
                 :tooltip="$root.getLabelText(field.tooltip)"
                 :type="fieldType(field)"
                 :options="field.options"
                 :required="field.required"
                 :inline="field.inline"
                 :width="field.width"
                 :height="field.height"
                 :value="formValues[field.id]"
                 :check-value="field.checkValue || field.value"
                 :itemnum="field.itemnum"
                 :min="field.min"
                 :max="field.max"
                 :cols="field.cols"
                 :default-value="field.defaultValue"
                 :disabled="hasOrder || !isEditable || field.readonly === true"
                 :is-printout="$root.isEmbeddedMode"
                 :suffix="field.suffix"
                 :force-validation="forceValidation"
                 :error="field.error || (field.label ? '' : 'Common.required')"
                 ref="field_control"
                 v-on:input="handleFormInput"
          ></field>
        </template>
      </template>
    </div>
  </div>
</template>
<script>
  import FormsMixin from '../Mixins/Forms.js';

  export default {
    name: 'ProjectMasterDataTab',
    mixins: [ FormsMixin ],
    props: {
      classes: { default: ''},
      form: { type: Array, default: [] },
      formValues: { type: Object, default: {} },
      itemNums: {},
      itemNumsAll: {},
      hasOrder: { type: Boolean, default: false },
      isEditable:  { type: Boolean, default: false },
      forceValidation: {type: Boolean },
    },
    data() {
      return {
        subforms: {},
        freshKey: Date.now(),
      }
    },
    mounted() {
      this.dropHiddenValues()
      this.$emit('valid', this.getFormStatus());
    },
    watch: {
      formValues(v) {
        this.formValues = v;
        this.$emit('valid', this.getFormStatus());
        this.$forceUpdate();
      },
      itemNumsAll(v) {
        this.itemNumsAll = v;
        this.$emit('valid', this.getFormStatus());
        this.$forceUpdate();
      }
    },
    computed: {
      requireAttachments () {
        return [].concat(
          ...this.form
            .filter(field => this.meetsRequirements(field))
            .map(field => this.getFieldRequiredAttachments(field)),
          ...(this.$refs.forms || []).map(x => x.requireAttachments),
        );
      },
    },
    methods: {
      refresh() {
        this.dropHiddenValues();

        if (Array.isArray(this.$refs.forms)) {
          this.$refs.forms.forEach(x => x.refresh());
        }

        this.$forceUpdate();

        this.$emit('valid', this.getFormStatus());
      },

      getFieldRequiredAttachments(field) {
        const r = [];
        if (field.requireAttachments) {
          r.push(requireAttachments);
        }

        if (Array.isArray(field.options)) {
          const option = field.options.find(x => x.value === this.formValues[field.id]);
          if (option && option.requireAttachments) {
            r.push(option.requireAttachments);
          }
        }

        return r;
      },

      handleSubFormStatus(id, isValid) {
        this.subforms[id] = isValid;
        this.$emit('valid', this.getFormStatus());
      },

      handleFormInput(value, field, itemnum) {
        this.formValues[field.name] = value;
        this.itemNums[field.name] = itemnum;
        this.$emit('input', value, field, itemnum);

        this.refresh();

        setTimeout(() => {
          this.$emit('valid', this.getFormStatus());
        }, 100)
      },

      getFormStatus() {
        return (this.$refs.field_control || []).every(field => field.isValid()) && Object.values(this.subforms).every(x => x);
      },

      getItemNums() {
        const keys = Object.values(this.form)
                .filter(field => this.meetsRequirements({requires: field.showIf || field.requires}))
                .map(x => x.id);

        const items = {};
        Object.keys(this.itemNums)
          .filter(x => keys.indexOf(x) >= 0)
          .map(x => this.itemNums[x])
          .filter(x => x)
          .forEach(itemnum => {
            items[itemnum] = (items[itemnum] || 0) + 1;
          });

        return [{
          section: null,
          items,
        }];
      },

      dropHiddenValues() {
        this.form.forEach(field => {
          if (!this.meetsRequirements({requires: field.showIf || field.requires})) {
            delete this.formValues[field.id]
          }
        })
      }
    }
  }
</script>
<style>
  .layout-container {
    display: flex;
    flex-direction: row;
    margin: 0 -8px;
  }

  .layout-column {
    flex: 1;
    padding: 0 8px;
  }

  @media screen and (max-width: 768px) {
    .layout-container {
      display: block;
      margin: 0 -8px;
    }
  }
</style>
